import React from 'react';
import { dockCardView, freeBeaconsLine } from '../features/queue/laserDock';

/**
 * The laser dock's card in Add Sheet (SheetTrackingModal; rules in features/queue/laserDock.js).
 *  - enforced, verdict ok: green "Docked: BCN-003 · TRK 000409 · … ✓ matches this job";
 *  - enforced, an ok with no fresh answer (`stale`): grey "Checking the dock…";
 *  - enforced, any other verdict: red, the API's message (+ one line per pallet);
 *  - advice (the switch is off): the same words, marked as advice; today's controls stay below.
 * `check` null = no card, only a refused save's message (`saveMessage`, shown until the next Confirm).
 * Free beacons (plugged in with no pallet) get a grey line. Enforced, a link opens the manager
 * override (manager PIN + reason + "This piece has no tracking number"; the dialog shows the
 * tracking number box under it unless that is ticked).
 */
export default function LaserDockCard({
  check, enforced, stale = false, saveMessage, typedNote = null, overrideNote = null,
  overrideOpen, onOpenOverride, onCancelOverride,
  managerPin, onManagerPin, reason, onReason, noLot = false, onNoLot = () => {},
}) {
  const view = check ? dockCardView(check) : null;
  const free = check ? freeBeaconsLine(check) : null;
  const ok = !!view && view.tone === 'ok';
  const tone = !view ? null : (ok && stale && enforced) ? 'stale' : ok ? 'ok' : enforced ? 'bad' : 'advice';
  const palette = tone === 'ok'
    ? { bg: '#e8f5e9', border: '#2e7d32', text: '#1b5e20' }
    : tone === 'bad'
      ? { bg: '#f8d7da', border: '#dc3545', text: '#58151c' }
      : tone === 'stale'
        ? { bg: 'var(--jq-bg-subtle, #f1f3f5)', border: '#adb5bd', text: '#495057' }
        : { bg: '#fff8e1', border: '#ffe082', text: '#5d4037' };
  if (!view && !saveMessage) return null;

  return (
    <div style={{ marginBottom: '12px' }}>
      {saveMessage && (
        <div data-testid="dock-save-refused" role="alert" style={{
          marginBottom: '6px', padding: '8px 10px', borderRadius: '6px', fontWeight: 'bold', fontSize: '0.95rem',
          backgroundColor: '#f8d7da', border: '2px solid #dc3545', color: '#58151c',
        }}>
          Not added: {saveMessage}
        </div>
      )}
      {view && (
        <div
          data-testid="dock-card"
          data-tone={tone}
          role={tone === 'bad' ? 'alert' : undefined}
          style={{
            padding: '10px 12px', borderRadius: '6px', backgroundColor: palette.bg,
            border: `2px solid ${palette.border}`, color: palette.text, fontSize: '0.85rem',
          }}
        >
          {!enforced && (
            <div style={{ fontWeight: 'bold', fontSize: '0.75rem', marginBottom: '4px' }}>
              Laser dock (advice: JobQ isn't enforcing the dock yet)
            </div>
          )}
          <div data-testid="dock-headline" style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
            {tone === 'stale' ? 'Checking the dock…' : view.headline}
          </div>
          {tone === 'stale' && (
            <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>
              No answer from the dock for a few seconds: Confirm waits for it.
            </div>
          )}
          {tone !== 'stale' && view.lines.length > 0 && (
            <ul data-testid="dock-pallets" style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
              {view.lines.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          )}
          {typedNote && (
            <div data-testid="dock-typed-note" style={{ fontSize: '0.85rem', marginTop: '6px', fontWeight: 'bold' }}>
              {typedNote}
            </div>
          )}
          {overrideNote && (
            <div data-testid="dock-override-note" style={{ fontSize: '0.85rem', marginTop: '6px', fontWeight: 'bold' }}>
              {overrideNote}
            </div>
          )}
          {enforced && tone === 'ok' && !overrideOpen && (
            <div style={{ fontSize: '0.8rem', marginTop: '6px' }}>
              Enter your PIN to record this sheet against this pallet.
            </div>
          )}
          {enforced && tone === 'bad' && !overrideOpen && (
            <div style={{ fontSize: '0.8rem', marginTop: '6px' }}>
              Fix it at the dock: this window checks again every 2 seconds.
            </div>
          )}
        </div>
      )}

      {free && (
        <div data-testid="dock-free" style={{ marginTop: '6px', fontSize: '0.8rem', color: 'var(--jq-text-muted, #666)' }}>
          {free}
        </div>
      )}

      {view && enforced && !overrideOpen && (
        <button
          type="button"
          onClick={onOpenOverride}
          style={{ background: 'none', border: 'none', padding: 0, marginTop: '6px', color: '#0a58ca', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
        >
          Dock not working, or this pallet's beacon is dead? Manager override
        </button>
      )}

      {view && enforced && overrideOpen && (
        <div data-testid="dock-override" style={{
          marginTop: '8px', padding: '8px', borderRadius: '4px',
          background: 'var(--jq-warning-bg, #fff3cd)', border: '1px solid var(--jq-warning-border, #ffe69c)',
        }}>
          <div style={{ fontSize: '0.85rem', marginBottom: '6px' }}>
            Manager override: the sheet is recorded against the tracking number entered below (or none), not the dock.
          </div>
          <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.85rem' }}>Manager PIN:</label>
          <input type="password" value={managerPin} onChange={(e) => onManagerPin(e.target.value)}
                 aria-label="Manager PIN"
                 autoComplete="off" style={{ width: '100%', padding: '6px', marginBottom: '6px' }} />
          <label style={{ display: 'block', fontWeight: 'bold', fontSize: '0.85rem' }}>Why override the dock?</label>
          <input type="text" value={reason} onChange={(e) => onReason(e.target.value)}
                 aria-label="Why override the dock?"
                 placeholder="e.g. beacon on this pallet is dead" style={{ width: '100%', padding: '6px' }} />
          <label style={{ display: 'flex', alignItems: 'center', marginTop: '6px', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={noLot} onChange={(e) => onNoLot(e.target.checked)}
                   style={{ marginRight: '8px' }} />
            <span>This piece has no tracking number</span>
          </label>
          <button
            type="button"
            onClick={onCancelOverride}
            style={{ background: 'none', border: 'none', padding: 0, marginTop: '6px', color: '#0a58ca', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
          >
            Cancel the override: use the dock
          </button>
        </div>
      )}
    </div>
  );
}
